import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useCurrentUser } from '@/app/providers/user';

// Wrap INSIDE RequireUser. Non-admins are bounced to the home page: this is a
// UI-level gate only, the real enforcement is require_admin_user on every admin
// API call, which a hidden/removed nav item alone can't guarantee.
//
// It used to say it did not handle the loading state because RequireUser
// resolves that first, and then read `user?.role`, which treats a null user as
// a refusal. Those are not the same thing. While the boot-time refresh is in
// flight `user` IS null, so a signed-in admin was one render away from being
// redirected to `/` — safe only for as long as every caller keeps it under a
// RequireUser, which is an invariant of App.tsx and not of this component.
//
// Holding is the correct answer to "I do not know yet" and costs nothing under
// the current nesting, where `status` is already 'authenticated' by the time
// this renders and the branch never runs. Same `<p>Loading…</p>` as
// RequireUser, so the two read alike if they are ever seen together.
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, status } = useCurrentUser();
  if (status === 'loading') {
    return <p>Loading…</p>;
  }
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
